import * as React from "react";
import { Box, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { SkillCategory, SkillCategoryType } from "../../types/skill";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useGetSkillCategoryByIdQuery,
  useAddSkillCategoryMutation,
  useEditSkillCategoryMutation,
} from "../../services/skill-category.service";
import { BaseModal, StyledTextField, Button, SelectBase, SelectOption } from "../../components/global";

const TYPE_OPTIONS: SelectOption[] = [
  { label: "Workz360", value: "workz360" },
  { label: "Home Edition", value: "home_edition" },
];

interface ModalSkillCategoryFormProps {
  open: boolean;
  onClose: () => void;
  data: SkillCategory | null;
  /** Default type when creating (from current page edition) */
  defaultType?: SkillCategoryType;
}

const ModalSkillCategoryForm = ({
  open,
  onClose,
  data,
  defaultType = "workz360",
}: ModalSkillCategoryFormProps) => {
  const { data: skill } = useGetSkillCategoryByIdQuery(data?.id!, {
    skip: !data?.id,
  });

  const [add, { isLoading: adding }] = useAddSkillCategoryMutation();
  const [edit, { isLoading: editing }] = useEditSkillCategoryMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      const typeValue =
        typeof payload.type === "object" && payload.type?.value
          ? payload.type.value
          : payload.type || "workz360";
      const body = {
        name: payload.name,
        description: payload.description ?? "",
        type: typeValue as "workz360" | "home_edition",
      };
      if (!data) await add(body).unwrap();
      else {
        await edit({ id: data.id, ...body }).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("name", data?.name || "");
      setValue("description", data?.description || "");
      setValue("type", data?.type || defaultType);
      clearErrors();
    }
  }, [data, open, setValue, clearErrors, defaultType]);

  React.useEffect(() => {
    if (skill) {
      setValue("name", skill?.name || "");
      setValue("description", skill?.description || "");
      setValue("type", skill?.type || defaultType);
    }
  }, [skill, setValue, defaultType]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Create" : "Edit"} Skill Category`}
      maxWidth="xs"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding || editing}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Create" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Category Name"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.name}
                  helperText={!!errors.name && "This field is required"}
                />
              )}
            />
            <Controller
              name="type"
              control={control}
              render={({ field: { onChange, value } }) => {
                const raw = value?.value ?? value ?? defaultType;
                const selectedOption =
                  TYPE_OPTIONS.find((o) => o.value === raw) ?? TYPE_OPTIONS[0];
                return (
                  <SelectBase
                    label="Type"
                    options={TYPE_OPTIONS}
                    value={selectedOption}
                    onChange={(v) => {
                      const option = Array.isArray(v) ? v[0] : v;
                      onChange(option && "value" in option ? option.value : option);
                    }}
                  />
                );
              }}
            />
            <Controller
              name="description"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Description"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  multiline
                  rows={3}
                  placeholder="Enter description (optional)"
                />
              )}
            />
          </Stack>
        </Box>
      </form>
    </BaseModal>
  );
};
export default ModalSkillCategoryForm;
